import React, { useEffect, useState } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';

const Filters: React.FC<{ className?: string }> = ({ className }) => {
    const { state } = useAppState();
    const { activityType, interests, selectedActivityTypes, selectedInterests } = state;
    const { setSelectedInterests, setSelectedActivityTypes } = useAppDispatchActions();
    const [interestsOpen, setInterestsOpen] = useState(false);
    const [typesOpen, setTypesOpen] = useState(false);

    const trackFilterItemClick = (name: string, type: 'interest' | 'activity type') => {
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
        (window as any).dataCoreLayer.push({
            event: 'od_planner_filter_click_list',
            click_text: name,
            click_classes: type,
        });
    };

    // Shared styling for the "closed" select display div:
    // This targets: div.MuiSelect-select ... MuiInputBase-inputSizeSmall
    const selectDisplaySx = {
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },

        // ✅ Padding on the SELECT DISPLAY *DIV* (not an input tag)
        '& .MuiSelect-select.MuiInputBase-inputSizeSmall': {
            padding: '9px 32px 9px 12px',
            lineHeight: '26px',
        },

        // Optional: keep your text styles here instead of inputProps.sx (either is fine)
        '& .MuiSelect-select': {
            fontSize: '14px',
            fontWeight: 500,
            textAlign: 'left',
        },
    } as const;

    useEffect(() => {
        if (!interestsOpen && !typesOpen) return;
        // --- GTM TRACKING START ---
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

        (window as any).dataCoreLayer.push({
            event: 'od_planner_filter_click_button',
            click_text: interestsOpen ? 'Interests' : 'Activity Types',
        });
        // --- GTM TRACKING END ---
    }, [interestsOpen, typesOpen]);

    return (
        <div className={`grid grid-cols-2 gap-2 rounded-[12px] bg-[var(--colour-gold)] p-[10px] ${className}`}>
            {/* Interests */}
            <div className='flex items-center gap-2'>
                <FormControl
                    fullWidth
                    size='small'
                    className='flex-1 rounded-[8px] border-none bg-white'>
                    <Select
                        open={interestsOpen}
                        onOpen={() => setInterestsOpen(true)}
                        onClose={() => setInterestsOpen(false)}
                        id='interests-select'
                        multiple
                        value={selectedInterests}
                        onChange={(event: SelectChangeEvent<unknown>) => {
                            const {
                                target: { value },
                            } = event;
                            const selectedValues = typeof value === 'string' ? value.split(',') : (value as string[]);
                            if (selectedValues.includes('__CLEAR_ALL__')) {
                                setSelectedInterests([]);
                                return;
                            }
                            setSelectedInterests(selectedValues);
                        }}
                        // ✅ Apply styles via the input component so they hit the right element
                        input={
                            <OutlinedInput
                                label='I am interested in...'
                                sx={selectDisplaySx}
                            />
                        }
                        displayEmpty
                        renderValue={(selected: any) => {
                            const selectedCount = selected.length;
                            if (selectedCount === 0 || selectedCount === interests.length) return 'All interests';
                            if (selectedCount === 1) {
                                const selectedItem = interests.find((i) => i.slug === selected[0]);
                                return selectedItem ? selectedItem.name : '1 Selected';
                            }
                            return `Interests (${selectedCount})`;
                        }}
                        MenuProps={{
                            slotProps: {
                                paper: {
                                    sx: {
                                        '& .MuiListItemText-primary': {
                                            fontSize: '16px',
                                        },
                                    },
                                },
                            },
                        }}>
                        {interests.map((interest) => (
                            <MenuItem
                                key={interest.slug}
                                value={interest.slug}
                                onClick={() => trackFilterItemClick(interest.name, 'interest')}>
                                <Checkbox checked={(selectedInterests || []).indexOf(interest.slug) > -1} />
                                <ListItemText primary={interest.name} />
                            </MenuItem>
                        ))}
                        <MenuItem
                            value='__CLEAR_ALL__'
                            onClick={() => trackFilterItemClick('Clear all', 'interest')}
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '8px !important',
                                marginTop: '8px',
                                fontWeight: 'bold',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        visibility: selectedInterests.length > 0 ? 'visible' : 'hidden',
                                    }}>
                                    <Checkbox
                                        checked={false}
                                        sx={{ visibility: 'hidden' }}
                                    />
                                    <ListItemText
                                        primary='Clear all'
                                        slotProps={{ primary: { fontWeight: 'bold', fontSize: '16px' } }}
                                    />
                                </Box>
                                <Typography
                                    component='span'
                                    variant='body1'
                                    sx={{
                                        'cursor': 'pointer',
                                        'fontWeight': 'bold',
                                        'fontSize': '16px',
                                        'mr': 2,
                                        'color': 'text.primary',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setInterestsOpen(false);
                                    }}>
                                    Close
                                </Typography>
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Session Types */}
            <div className='flex items-center gap-2'>
                <FormControl
                    fullWidth
                    size='small'
                    className='flex-1 rounded-[8px] bg-white'>
                    <Select
                        open={typesOpen}
                        onOpen={() => setTypesOpen(true)}
                        onClose={() => setTypesOpen(false)}
                        id='types-select'
                        multiple
                        value={selectedActivityTypes}
                        onChange={(event: SelectChangeEvent<unknown>) => {
                            const {
                                target: { value },
                            } = event;
                            const selectedValues = typeof value === 'string' ? value.split(',') : (value as string[]);
                            if (selectedValues.includes('__CLEAR_ALL__')) {
                                setSelectedActivityTypes([]);
                                return;
                            }
                            setSelectedActivityTypes(selectedValues);
                        }}
                        input={
                            <OutlinedInput
                                label="I'd like to see..."
                                sx={selectDisplaySx}
                            />
                        }
                        displayEmpty
                        renderValue={(selected: any) => {
                            const selectedCount = selected.length;
                            if (selectedCount === 0 || selectedCount === activityType.length)
                                return 'All session types';
                            if (selectedCount === 1) {
                                const selectedItem = activityType.find((t) => t.slug === selected[0]);
                                return selectedItem ? selectedItem.name : '1 Selected';
                            }
                            return `Session Types (${selectedCount})`;
                        }}
                        MenuProps={{
                            slotProps: {
                                paper: {
                                    sx: {
                                        '& .MuiListItemText-primary': {
                                            fontSize: '16px',
                                        },
                                    },
                                },
                            },
                        }}>
                        {activityType.map((type) => (
                            <MenuItem
                                key={type.slug}
                                value={type.slug}
                                onClick={() => trackFilterItemClick(type.name, 'activity type')}>
                                <Checkbox checked={(selectedActivityTypes || []).indexOf(type.slug) > -1} />
                                <ListItemText primary={type.name} />
                            </MenuItem>
                        ))}
                        <MenuItem
                            value='__CLEAR_ALL__'
                            onClick={() => trackFilterItemClick('Clear all', 'activity type')}
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '8px !important',
                                marginTop: '8px',
                                fontWeight: 'bold',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        visibility: selectedActivityTypes.length > 0 ? 'visible' : 'hidden',
                                    }}>
                                    <Checkbox
                                        checked={false}
                                        sx={{ visibility: 'hidden' }}
                                    />
                                    <ListItemText
                                        primary='Clear all'
                                        slotProps={{ primary: { fontWeight: 'bold' } }}
                                    />
                                </Box>
                                <Typography
                                    component='span'
                                    variant='body1'
                                    sx={{
                                        'cursor': 'pointer',
                                        'fontWeight': 'bold',
                                        'fontSize': '16px',
                                        'mr': 2,
                                        'color': 'text.primary',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTypesOpen(false);
                                    }}>
                                    Close
                                </Typography>
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>
            </div>
        </div>
    );
};

export default Filters;
